import type { Ref } from "vue";
import { until } from "@vueuse/core";

import { cache, CacheClosedError } from "@/services/cache";
import type { InitLatch } from "@/stores/init-latch";

export interface CacheReloadOptions< Row > {
    /** The cache store the sync worker wrote. */
    binding: { isEmpty(): Promise< boolean >; all(): Promise< Row[] > };
    /** The store's own first-load flag: a load in flight is joined, never raced. */
    loading: Readonly< Ref< boolean > >;
    latch: InitLatch;
    /** False when there is nothing to re-read for (the worker only syncs for a signed-in member). */
    canRead(): boolean;
    /**
     * Builds the complete catalog from the cached rows off to the side and swaps it in with one assignment, replacing
     * (never merging into) what is there, so an unpublished or de-entitled item drops out.
     */
    apply( rows: Row[] ): void;
    /**
     * The cache holds nothing for this store: the worker has just cleared it for a new owner. The previous member's
     * rows must go, so this is the store's own reset() followed by its first load.
     */
    reinitialize(): Promise< void >;
    /** A read that failed for any reason other than an eviction; the catalog in memory is kept. */
    report( err: unknown ): void;
}

export interface CacheReload {
    reloadFromCacheAsync(): Promise< void >;
    /** Called from the store's reset(): drops a pending re-read, and one in flight never applies its result. */
    cancel(): void;
}

/**
 * tmi-e7w.22 (DP-22) for the content stores: re-read a catalog after the sync worker wrote it, without emptying it.
 *
 * UPDATE_COMPLETE used to run `reset()` + `initialize()`, which emptied the catalog and raised the store-wide loading
 * flag for the whole IndexedDB read: the article, alert or webinar being read swapped to a spinner, lists flashed
 * 'No results found', the bell read "all caught up" and a playing webinar unmounted. Now the loading flag and the
 * catalog are left alone until the new catalog is complete.
 *
 * Requests that arrive during a read coalesce into one more read. A first load in flight is joined and then read
 * again, since it may have read the cache before the write. A CacheClosedError stops quietly; any other cache failure
 * latches the store (tmi-7be) and is reported once per read, and the catalog shown stays as it was.
 */
export function createCacheReload< Row >( options: CacheReloadOptions< Row > ): CacheReload {
    // Bumped by cancel(): a read started for an earlier session never applies its rows to this one.
    let generation = 0;
    let requested = false;
    let inFlight: Promise< void > | null = null;

    function reloadFromCacheAsync(): Promise< void > {
        requested = true;
        if ( !inFlight ) {
            const run: Promise< void > = drain().finally( () => {
                if ( inFlight === run ) {
                    inFlight = null;
                }
            } );
            inFlight = run;
        }

        return inFlight;
    }

    async function drain(): Promise< void > {
        const runGeneration = generation;
        while ( requested && runGeneration === generation ) {
            requested = false;
            await readOnce( () => runGeneration === generation );
        }
    }

    async function readOnce( isCurrent: () => boolean ): Promise< void > {
        // An evicted tab is already reloading.
        if ( 'evicted' === options.latch.halted.value || !options.canRead() ) {
            return;
        }

        if ( options.loading.value ) {
            await until( options.loading ).toBe( false );
            if ( !isCurrent() ) {
                return;
            }
        }

        try {
            await cache.ready();

            if ( await options.binding.isEmpty() ) {
                if ( isCurrent() ) {
                    await options.reinitialize();
                }
                return;
            }

            const rows: Row[] = await options.binding.all();
            if ( !isCurrent() ) {
                return;
            }

            options.apply( rows );
            // The read worked, so an earlier cache failure no longer stands in the way of initialize().
            options.latch.clear();
        } catch ( err: unknown ) {
            if ( !isCurrent() ) {
                return;
            }

            // A newer build evicted this context: stop quietly and keep what is shown until the page reloads.
            if ( err instanceof CacheClosedError ) {
                options.latch.halt( err );
                return;
            }

            // Only a CacheError latches; the next sync that writes asks for another read either way.
            options.latch.halt( err );
            options.report( err );
        }
    }

    function cancel(): void {
        generation += 1;
        requested = false;
        inFlight = null;
    }

    return { reloadFromCacheAsync, cancel };
}
